import { useRef, useEffect } from 'react'
import type {
  ObstacleHandlerData,
  ObstaclePose,
  ObstaclePoint,
} from '@/types/obstacle'
import {
  combineObstacleSourceBits,
  OBSTACLE_SENSOR_LABELS,
} from '@/types/obstacle'

// 障害物エラー発生時のフォークアイコン・反応センサー位置・障害物位置を描画する
// （描画ロジックは fork_webapp_react の drawForklift / drawObstacleIndicators /
//   drawObstaclePoints から移植。フォークリフト形状のみ対応）

const MIN_VIEW_WIDTH_M = 6 // 機体中心の最小表示範囲（メートル）
const MIN_ROBOT_ICON_SIZE_PX = 40

function collectObstaclePoints(data: ObstacleHandlerData): ObstaclePoint[] {
  return [
    data.obstaclePointFront,
    data.obstaclePointRear,
    data.obstaclePointTrajectory,
  ].filter((p): p is ObstaclePoint => p !== null && p !== undefined)
}

function drawForklift(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  theta: number,
  color: string,
  size: number,
  bodyColor = '#4b5563'
) {
  // 寸法定義（size基準）
  const bodyW = size * 1.2
  const bodyH = size * 1.1
  const forkLen = size * 1.1
  const forkW = size * 0.25
  const forkGap = size * 0.55 // フォーク爪の中心間距離
  const cwW = size * 1.2 // 本体（青）幅
  const cwH = size * 0.4 // 本体（青）高さ
  const r = size * 0.1 // 角丸半径

  ctx.save()
  ctx.translate(x, y)
  // Canvas座標系: Y軸下向き。theta=0で右向き（ROS座標系）
  // -theta でROS座標→Canvas座標に変換し、-PI/2 で「上向き=前方」に補正
  ctx.rotate(-theta - Math.PI / 2)
  // 描画中心をオフセット
  ctx.translate(0, size * 0.5)

  // --- フォーク爪（前方・2本の角丸長方形） ---
  ctx.fillStyle = color
  ctx.beginPath()
  ctx.roundRect(
    -forkGap / 2 - forkW / 2,
    -bodyH / 2 - forkLen + 15,
    forkW,
    forkLen,
    r
  )
  ctx.fill()
  ctx.beginPath()
  ctx.roundRect(
    forkGap / 2 - forkW / 2,
    -bodyH / 2 - forkLen + 15,
    forkW,
    forkLen,
    r
  )
  ctx.fill()

  // --- 本体（グレー） ---
  ctx.fillStyle = bodyColor
  ctx.beginPath()
  ctx.roundRect(-bodyW / 2, -bodyH / 2, bodyW, bodyH, r)
  ctx.fill()

  // --- 本体（青） ---
  ctx.fillStyle = color
  ctx.beginPath()
  ctx.roundRect(-cwW / 2, bodyH / 2 - cwH * 0.8, cwW, cwH, r)
  ctx.fill()

  ctx.restore()
}

// 反応したセンサーをフォークアイコン上の対応する位置に赤い点で表示する
function drawObstacleIndicators(
  ctx: CanvasRenderingContext2D,
  data: ObstacleHandlerData,
  robotX: number,
  robotY: number,
  robotTheta: number,
  shapeSize: number
): void {
  const allSourceBits = combineObstacleSourceBits(data)
  if (allSourceBits === 0) return

  const angle = -robotTheta - Math.PI / 2
  const cos = Math.cos(angle)
  const sin = Math.sin(angle)
  const offsetY = shapeSize * 0.5
  const baseX = robotX - offsetY * sin
  const baseY = robotY + offsetY * cos
  const rotatePoint = (lx: number, ly: number) => ({
    x: baseX + lx * cos - ly * sin,
    y: baseY + lx * sin + ly * cos,
  })

  const bodyH = shapeSize * 1.0
  const forkLen = shapeSize * 1.2
  const forkGap = shapeSize * 0.55
  const cwH = shapeSize * 0.6
  const cwY = bodyH / 2 - cwH * 0.3 + cwH / 2

  const sensorPositions = [
    { bit: 0x01, ...rotatePoint(shapeSize * 0.6, cwY) }, // LiDAR L
    { bit: 0x02, ...rotatePoint(-shapeSize * 0.6, cwY) }, // LiDAR R
    { bit: 0x04, ...rotatePoint(0, -bodyH / 2) }, // LiDAR Rear
    { bit: 0x08, ...rotatePoint(forkGap / 2, -(bodyH / 2 + forkLen)) }, // Tip L
    { bit: 0x10, ...rotatePoint(-forkGap / 2, -(bodyH / 2 + forkLen)) }, // Tip R
  ]

  const dotRadius = shapeSize * 0.2

  for (const sensor of sensorPositions) {
    if (allSourceBits & sensor.bit) {
      ctx.fillStyle = 'rgba(239, 68, 68, 0.9)'
      ctx.beginPath()
      ctx.arc(sensor.x, sensor.y, dotRadius, 0, Math.PI * 2)
      ctx.fill()
      ctx.strokeStyle = '#ffffff'
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.arc(sensor.x, sensor.y, dotRadius, 0, Math.PI * 2)
      ctx.stroke()
    }
  }
}

// 障害物の検出位置を黄色い四角で描画する（点滅なしの静止表示）
function drawObstaclePoints(
  ctx: CanvasRenderingContext2D,
  points: ObstaclePoint[],
  worldToCanvas: (x: number, y: number) => { x: number; y: number },
  pixelsPerMeter: number
): void {
  const half = pixelsPerMeter * 0.2

  for (const point of points) {
    const pos = worldToCanvas(point.x, point.y)

    ctx.fillStyle = 'rgba(234, 179, 8, 0.7)'
    ctx.fillRect(pos.x - half, pos.y - half, half * 2, half * 2)

    ctx.strokeStyle = 'rgba(255, 255, 255, 1.0)'
    ctx.lineWidth = 2
    ctx.strokeRect(pos.x - half, pos.y - half, half * 2, half * 2)
  }
}

export default function ObstacleSensorCanvas({
  obstacleHandler,
  pose,
  width = 400,
  height = 300,
}: {
  obstacleHandler: ObstacleHandlerData
  pose: ObstaclePose | null
  width?: number
  height?: number
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // 表示サイズ（CSS px）× devicePixelRatio で描画バッファを確保し、
    // 拡大表示でぼやけないようにする。以降の描画座標は CSS px。
    const dpr = window.devicePixelRatio || 1
    const w = canvas.clientWidth || width
    const h = Math.round((w * height) / width)
    canvas.width = Math.round(w * dpr)
    canvas.height = Math.round(h * dpr)
    canvas.style.height = `${h}px`
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

    ctx.clearRect(0, 0, w, h)
    ctx.fillStyle = '#f8fafc'
    ctx.fillRect(0, 0, w, h)

    // 機体を中心に表示。障害物位置が遠い場合は全体が入るよう範囲を広げる
    const obstaclePoints = pose ? collectObstaclePoints(obstacleHandler) : []
    let maxDist = 0
    if (pose) {
      for (const p of obstaclePoints) {
        const d = Math.hypot(p.x - pose.x, p.y - pose.y)
        if (d > maxDist) maxDist = d
      }
    }
    const viewWidthM = Math.max(MIN_VIEW_WIDTH_M, (maxDist + 1.5) * 2)
    const pixelsPerMeter = Math.min(w, h) / viewWidthM

    const cx = w / 2
    const cy = h / 2
    const px = pose?.x ?? 0
    const py = pose?.y ?? 0
    const theta = pose?.theta ?? Math.PI / 2
    const worldToCanvas = (x: number, y: number) => ({
      x: cx + (x - px) * pixelsPerMeter,
      y: cy - (y - py) * pixelsPerMeter,
    })

    // 1mグリッド（表示範囲が広いときは5m）
    const gridStep = viewWidthM > 15 ? 5 : 1
    ctx.strokeStyle = '#e2e8f0'
    ctx.lineWidth = 1
    const halfWM = w / pixelsPerMeter / 2
    const halfHM = h / pixelsPerMeter / 2
    const xStart = Math.ceil((px - halfWM) / gridStep) * gridStep
    const yStart = Math.ceil((py - halfHM) / gridStep) * gridStep
    for (let x = xStart; x <= px + halfWM; x += gridStep) {
      const { x: gx } = worldToCanvas(x, 0)
      ctx.beginPath()
      ctx.moveTo(gx, 0)
      ctx.lineTo(gx, h)
      ctx.stroke()
    }
    for (let y = yStart; y <= py + halfHM; y += gridStep) {
      const { y: gy } = worldToCanvas(0, y)
      ctx.beginPath()
      ctx.moveTo(0, gy)
      ctx.lineTo(w, gy)
      ctx.stroke()
    }

    const shapeSize = Math.max(pixelsPerMeter, MIN_ROBOT_ICON_SIZE_PX)

    drawObstaclePoints(ctx, obstaclePoints, worldToCanvas, pixelsPerMeter)
    drawForklift(ctx, cx, cy, theta, '#60a5fa', shapeSize)
    drawObstacleIndicators(ctx, obstacleHandler, cx, cy, theta, shapeSize)
  }, [obstacleHandler, pose, width, height])

  const allSourceBits = combineObstacleSourceBits(obstacleHandler)
  const activeSensors = OBSTACLE_SENSOR_LABELS.filter(
    (s) => allSourceBits & s.bit
  )

  return (
    <div className="space-y-2">
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className="w-full border border-gray-200 rounded-lg"
      />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
        <div className="flex items-center gap-1">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500 border border-white" />
          <span>
            反応したセンサー
            {activeSensors.length > 0 &&
              `（${activeSensors.map((s) => s.label).join('・')}）`}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2.5 h-2.5 bg-yellow-500 border border-white" />
          <span>障害物位置</span>
        </div>
      </div>
    </div>
  )
}
